import { AbstractMesh, Angle, Color3, Color4, GizmoManager, InstancedMesh, Mesh, MeshBuilder, PBRMetallicRoughnessMaterial, Scene, SceneLoader, ShadowGenerator, Vector3 } from "@babylonjs/core";
import { PartTransformation } from "../../components/BuildingConf/models/part-transformation";
import { GroupControlsModel } from "../group-controls.model";
import { MeshCache } from "../mesh-cache.model";
import { accuracyEnum } from "../models/accuracy.enum";
import { MetadataModel } from "../parent.model";
import { CloudStorageService } from "../firebase/cloud-storage.service";
import { Point3 } from "../models/point.type";

export class ModelLoaderService {

    private meshCache: MeshCache[] = [];
    private shadowGenerator: ShadowGenerator;
    private scene: Scene;
    private drawingPolyline = false;
    public groupControls: GroupControlsModel[] = [];
    private customMeshMaterial: PBRMetallicRoughnessMaterial;
    private currentAccuracy: accuracyEnum;

    private selectedSubgroupIds: string[] = [];
    private highlightedSubgroupIds: string[] = [];

    private edgesConf = {
        standard: {
            color: new Color4(0.2, 0.2, 0.2, 1),
            width: 10,
        },
        selected: {
            color: new Color4(0, 1, 0, 1),
            width: 20,
        },
        highlighted: {
            color: new Color4(1, 1, 0.2, 1),
            width: 20,
        }
    }

    posOrRotOfGroupChangedCallback: (res: {
        position: Point3,
        rotation: Point3,
        groupId: number | string;
    }) => void;
    pickedGroupCallback: (groupId: string | number) => void;
    scaleChangedCallback: (res: { scale: Point3, groupId: string | number }) => void;

    constructor(scene: Scene, shadowGenerator: ShadowGenerator) {
        this.scene = scene;
        this.shadowGenerator = shadowGenerator;
        this.customMeshMaterial = new PBRMetallicRoughnessMaterial('customMeshes', this.scene);
        this.customMeshMaterial.baseColor = new Color3(0.5, 0.5, 1);
        this.customMeshMaterial.roughness = 0.9;
        this.customMeshMaterial.metallic = 0.3;
    }

    highlightGroupsSugroup(groupId: string, subgroupId: string, state: 'highlight' | 'select' | 'standard') {
        const gc = this.groupControls.find(g => g.groupId === groupId);
        if (gc && gc.gizmoMesh) {
            const children = gc.gizmoMesh.getChildMeshes();

            this.cleanHighlights(children, groupId);

            children.forEach(m => {
                if (m.metadata && m.metadata.groupId === groupId && m.metadata.subgroupId === subgroupId) {
                    if (state === 'select') {
                        this.selectedSubgroupIds = [subgroupId];
                        this.highlightedSubgroupIds = [];
                        if (m.edgesRenderer === null) {
                            m.enableEdgesRendering();
                        }
                        m.edgesColor = this.edgesConf.selected.color;
                        m.edgesWidth = this.edgesConf.selected.width;
                    } else if (state === 'highlight') {
                        this.highlightedSubgroupIds = [subgroupId];
                        this.selectedSubgroupIds = [];
                        if (m.edgesRenderer === null) {
                            m.enableEdgesRendering();
                        }
                        m.edgesColor = this.edgesConf.highlighted.color;
                        m.edgesWidth = this.edgesConf.highlighted.width;
                    } else if (state === 'standard' && this.currentAccuracy === accuracyEnum.conceptual) {
                        this.highlightedSubgroupIds = [];
                        this.selectedSubgroupIds = [];
                        m.edgesColor = this.edgesConf.standard.color;
                        m.edgesWidth = this.edgesConf.standard.width;
                    } else {
                        this.highlightedSubgroupIds = [];
                        this.selectedSubgroupIds = [];
                        // m.edgesColor = new Color4(0, 0, 0, 0);
                        m.edgesWidth = 0;
                    }
                }
            });
        }
    }

    removeHighlights(groupId) {
        const gc = this.groupControls.find(g => g.groupId === groupId);
        const children = gc.gizmoMesh.getChildMeshes();

        this.cleanHighlights(children, groupId);
    }

    private cleanHighlights(children: AbstractMesh[], groupId: string) {
        if ((this.selectedSubgroupIds && this.selectedSubgroupIds.length) || this.highlightedSubgroupIds && this.highlightedSubgroupIds.length) {
            children.forEach(m => {
                // clean up existing
                if (m.metadata && m.metadata.groupId === groupId && (this.selectedSubgroupIds.includes(m.metadata.subgroupId) || this.highlightedSubgroupIds.includes(m.metadata.subgroupId))) {
                    if (this.currentAccuracy === accuracyEnum.conceptual) {
                        m.edgesColor = this.edgesConf.standard.color;
                        m.edgesWidth = this.edgesConf.standard.width;
                    } else {
                        m.edgesColor = new Color4(0, 0, 0, 0);
                        m.edgesWidth = 0;
                    }
                }
            });

            this.highlightedSubgroupIds = [];
            this.selectedSubgroupIds = [];
        }
    }

    loadObjToScene(fileName: string, rootUrl: string, scale: number, color: string, keepOriginalMaterial = false): Promise<MeshCache> {
        let promise;
        const findMesh = this.meshCache.find(m => m.url === rootUrl + fileName);

        if (!findMesh || !findMesh.meshPromise) {
            promise = CloudStorageService.getEnvDownloadUrl(rootUrl + fileName).then(downloadUrl => {

                return new Promise<MeshCache>((resolve, reject) => {
                    SceneLoader.ImportMeshAsync('', downloadUrl, '', this.scene).then((res) => {
                        if (res.meshes.length === 1) {
                            res.meshes.forEach(mesh => {
                                mesh.scaling = new Vector3(scale, scale, scale);
                                mesh.isVisible = false;
                                mesh.isPickable = false;
                                mesh.receiveShadows = true;
                                mesh.metadata = { groupId: 'baseModelMesh' };
                                if (!keepOriginalMaterial) {
                                    var pbr = new PBRMetallicRoughnessMaterial("pbr" + rootUrl + fileName, this.scene);
                                    pbr.metallic = 0.5;
                                    pbr.roughness = 0.9;
                                    const color3 = Color3.FromHexString(color);
                                    pbr.baseColor = color3;
                                    mesh.material = pbr;
                                } else {
                                    if (mesh.material) {
                                        const mat = mesh.material as PBRMetallicRoughnessMaterial;
                                        mat.roughness = 1;
                                        mat.metallic = 0;
                                        mat._environmentIntensity = 0;
                                    }
                                }
                                const foundMesh = this.meshCache.find(m => m.url === rootUrl + fileName);
                                foundMesh.mesh = mesh as Mesh;
                                resolve(foundMesh);
                            });
                        } else if (res.meshes.length > 1) {

                            const baseMesh = MeshBuilder.CreateBox('dada' + Math.random(), { size: 0.0001 }, this.scene);
                            baseMesh.isVisible = false;
                            baseMesh.isPickable = false;
                            baseMesh.metadata = { groupId: 'baseModelMesh' };
                            baseMesh.scaling = new Vector3(scale, scale, scale);
                            res.meshes.forEach(mesh => {
                                if (mesh.getTotalVertices() > 0 && mesh.getTotalIndices() > 0) {
                                    mesh.isVisible = false;
                                    mesh.receiveShadows = true;
                                    mesh.isPickable = false;
                                    mesh.parent = baseMesh;
                                    if (mesh.material) {
                                        const mat = mesh.material as PBRMetallicRoughnessMaterial;
                                        mat.metallic = 0;
                                        mat.roughness = 1;
                                    }
                                }
                            })
                            const foundMesh = this.meshCache.find(m => m.url === rootUrl + fileName);
                            foundMesh.mesh = baseMesh as Mesh;
                            resolve(foundMesh);
                        }
                    })

                });
            })

            if (!findMesh) {
                const cacheMesh = {
                    url: rootUrl + fileName,
                    mesh: undefined,
                    instancedMeshes: [],
                    meshPromise: promise
                };
                this.meshCache.push(cacheMesh);
            }
        } else if (findMesh && !findMesh.mesh && findMesh.meshPromise) {
            promise = findMesh.meshPromise;
        } else if (findMesh && findMesh.mesh) {
            promise = Promise.resolve(findMesh);
        }

        return promise;
    }

    getInstanceMesh(
        fileName: string,
        rootUrl: string,
        origin: Point3,
        metadata: MetadataModel,
        rotationAngle = 0,
        locationInSite: { position: Point3, rotation: Point3 },
        partTransformation: PartTransformation,
        accuracy: accuracyEnum,
        color: string,
        keepOriginalMaterial = false,
    ): Promise<InstancedMesh> {

        const promise = new Promise<InstancedMesh>((resolve, reject) => {

            const findMesh = this.meshCache.find(m => m.url.includes(rootUrl + fileName));

            const loadInstance = (cachedMesh: MeshCache) => {
                this.currentAccuracy = accuracy;
                const children = cachedMesh.mesh.getChildMeshes();
                let instance: InstancedMesh;
                instance = cachedMesh.mesh.createInstance((cachedMesh.instancedMeshes.length + 1).toString());
                if (children && children.length > 0) {
                    instance.isVisible = false;
                    children.forEach((child, index) => {
                        const childInstance = (child as Mesh).createInstance((cachedMesh.instancedMeshes.length + index).toString());
                        childInstance.parent = instance;
                        childInstance.metadata = { groupId: metadata.groupId, subgroupId: metadata.subgroupId };
                        childInstance.isVisible = true;
                        if (this.selectedSubgroupIds.includes(metadata.subgroupId)) {
                            childInstance.edgesWidth = this.edgesConf.selected.width;
                            childInstance.edgesColor = this.edgesConf.selected.color;
                            childInstance.enableEdgesRendering();
                        } else if (this.highlightedSubgroupIds.includes(metadata.subgroupId)) {
                            childInstance.edgesWidth = this.edgesConf.highlighted.width;
                            childInstance.edgesColor = this.edgesConf.highlighted.color;
                            childInstance.enableEdgesRendering();
                        } else if (accuracy === accuracyEnum.conceptual) {
                            childInstance.edgesColor = this.edgesConf.standard.color;
                            childInstance.edgesWidth = this.edgesConf.standard.width;
                            childInstance.enableEdgesRendering();
                        } else {
                            childInstance.disableEdgesRendering();
                            childInstance.edgesWidth = 0;
                        }
                    })
                }
                this.shadowGenerator?.addShadowCaster(instance, true);
                let groupControl = this.groupControls.find(s => s.groupId === metadata.groupId);
                if (!groupControl) {
                    const gizmoMesh = new Mesh('gizmo' + metadata.groupId, this.scene);
                    gizmoMesh.position = new Vector3(locationInSite.position[0], locationInSite.position[1], locationInSite.position[2]);
                    gizmoMesh.rotation = new Vector3(locationInSite.rotation[0], locationInSite.rotation[1], locationInSite.rotation[2]);

                    gizmoMesh.isVisible = false;
                    gizmoMesh.metadata = { groupId: metadata.groupId };
                    instance.parent = gizmoMesh;
                    const groupGizmo = this.createGizmo(gizmoMesh, metadata.groupId);
                    if (groupGizmo) {
                        groupControl = { groupId: metadata.groupId, gizmoMesh, groupGizmo };
                        this.groupControls.push(groupControl);
                    }
                }

                this.applyPartTransformation(partTransformation, metadata, groupControl, instance);

                if (accuracy === accuracyEnum.conceptual) {
                    instance.edgesWidth = this.edgesConf.standard.width;
                    instance.edgesColor = this.edgesConf.standard.color;
                    instance.enableEdgesRendering();
                } else {
                    instance.disableEdgesRendering();
                    instance.edgesWidth = 0;
                }

                instance.metadata = metadata;
                instance.rotateAround(instance.position, new Vector3(0, -1, 0), Angle.FromDegrees(rotationAngle).radians());
                instance.position = new Vector3(origin[0], origin[1], origin[2]);

                if (!partTransformation) {
                    instance.parent = groupControl.gizmoMesh;
                }

                resolve(instance);
            }

            if (findMesh && findMesh.mesh) {
                loadInstance(findMesh)
            } else {
                let scale = fileName.includes('.glb') ? 1 : 0.001;
                this.loadObjToScene(fileName, rootUrl, scale, color, keepOriginalMaterial).then((s) => {
                    loadInstance(s)
                })
            }
        })
        return promise;
    }

    disableBoundingBoxGizmo(groupId: string | number) {
        const group = this.groupControls.find(g => g.groupId === groupId);
        if (group) {
            group.groupGizmo.boundingBoxGizmoEnabled = false;
        }
    }

    enableBoundingBoxGizmo(groupId: string | number) {
        const group = this.groupControls.find(g => g.groupId === groupId);
        if (group) {
            group.groupGizmo.boundingBoxGizmoEnabled = true;
        }
    }

    createGizmo(mesh: Mesh, groupId: string | number) {
        if (this.scene) {
            var gizmoManager = new GizmoManager(this.scene);
            gizmoManager.positionGizmoEnabled = true;
            gizmoManager.rotationGizmoEnabled = true;
            gizmoManager.boundingBoxGizmoEnabled = true;

            gizmoManager.boundingBoxDragBehavior.dragDeltaRatio = 0;
            gizmoManager.boundingBoxDragBehavior.rotateDraggedObject = false;
            gizmoManager.boundingBoxDragBehavior.detach();
            gizmoManager.boundingBoxDragBehavior.detachCameraControls = false;

            if (gizmoManager.gizmos.positionGizmo) {
                gizmoManager.gizmos.positionGizmo.yPlaneGizmo.isEnabled = true;
                gizmoManager.gizmos.positionGizmo.yGizmo.isEnabled = false;
                // gizmoManager.gizmos.positionGizmo.snapDistance = 1;

                gizmoManager.gizmos.positionGizmo.onDragEndObservable.add(() => {
                    if (gizmoManager.attachableMeshes && gizmoManager.attachableMeshes.length > 0) {
                        const atMesh = gizmoManager.attachableMeshes[0];
                        const eulers = atMesh.absoluteRotationQuaternion.toEulerAngles();
                        this.posOrRotOfGroupChangedCallback({
                            position: [atMesh.position.x, 0, atMesh.position.z],
                            rotation: [eulers.x, eulers.y, eulers.z],
                            groupId
                        })
                    }
                });

            }
            if (gizmoManager.gizmos.rotationGizmo) {
                gizmoManager.gizmos.rotationGizmo.xGizmo.isEnabled = false;
                gizmoManager.gizmos.rotationGizmo.zGizmo.isEnabled = false;
                // gizmoManager.gizmos.rotationGizmo.snapDistance = Angle.FromDegrees(1).radians();
                gizmoManager.gizmos.rotationGizmo.onDragEndObservable.add(() => {
                    if (gizmoManager.attachableMeshes && gizmoManager.attachableMeshes.length > 0) {
                        const atMesh = gizmoManager.attachableMeshes[0];
                        const eulers = atMesh.absoluteRotationQuaternion.toEulerAngles();
                        this.posOrRotOfGroupChangedCallback({
                            position: [atMesh.position.x, 0, atMesh.position.z],
                            rotation: [eulers.x, eulers.y, eulers.z],
                            groupId
                        })
                    }
                });
            }
            gizmoManager.scaleGizmoEnabled = false;
            gizmoManager.clearGizmoOnEmptyPointerEvent = true;

            gizmoManager.attachableMeshes = [mesh];

            if (gizmoManager.gizmos.boundingBoxGizmo) {
                gizmoManager.gizmos.boundingBoxGizmo.updateGizmoPositionToMatchAttachedMesh = true;
                gizmoManager.gizmos.boundingBoxGizmo.rotationSphereSize = 0;
                gizmoManager.gizmos.boundingBoxGizmo.scaleBoxSize = 1.2;
                gizmoManager.gizmos.boundingBoxGizmo.axisFactor._x = 4;
                gizmoManager.gizmos.boundingBoxGizmo.axisFactor._y = 5;

                const scaleBoxes = gizmoManager.gizmos.boundingBoxGizmo['_scaleBoxesParent'].getChildMeshes();
                scaleBoxes[0].setEnabled(false);
                scaleBoxes[1].setEnabled(false);
                scaleBoxes[2].setEnabled(false);
                scaleBoxes[3].setEnabled(false);
                scaleBoxes[4].setEnabled(false);
                scaleBoxes[5].setEnabled(false);
                scaleBoxes[6].setEnabled(false);
                scaleBoxes[7].setEnabled(false);
                scaleBoxes[8].setEnabled(true);
                scaleBoxes[9].setEnabled(false);
                scaleBoxes[10].setEnabled(false);
                scaleBoxes[11].setEnabled(true);
                scaleBoxes[12].setEnabled(false);
                scaleBoxes[13].setEnabled(false);

                gizmoManager.gizmos.boundingBoxGizmo.onScaleBoxDragEndObservable.add((e, es) => {
                    const gizmoScale: Vector3 = (gizmoManager.gizmos.boundingBoxGizmo as any)._existingMeshScale;
                    this.scaleChangedCallback({
                        scale: [gizmoScale.x, gizmoScale.y, gizmoScale.z],
                        groupId
                    });
                });
            }

            gizmoManager.onAttachedToMeshObservable.add((ed, _) => {
                if (ed && !this.drawingPolyline) {
                    this.pickedGroupCallback(groupId);
                }
            });

            return gizmoManager;
        }
    }

    private applyPartTransformation(partTransformation: PartTransformation, metadata: MetadataModel, groupControl: GroupControlsModel, customMesh: Mesh | InstancedMesh) {
        if (partTransformation) {
            const name = 'part-' + metadata.groupId + '-' + partTransformation.id;
            let partWithName = this.scene?.getMeshByName(name);
            if (!partWithName && groupControl) {
                partWithName = new Mesh(name, this.scene);
                partWithName.isPickable = false;
                partWithName.scaling = new Vector3(partTransformation.scale[0], partTransformation.scale[1], partTransformation.scale[2]);
                partWithName.rotation = new Vector3(partTransformation.rotation[0], partTransformation.rotation[1], partTransformation.rotation[2]);
                partWithName.parent = groupControl.gizmoMesh;
                partWithName.position = new Vector3(partTransformation.position[0], partTransformation.position[1], partTransformation.position[2]);
                partWithName.metadata = { groupId: metadata.groupId };
            }
            customMesh.parent = partWithName;
        }
    }

    cleanAllMeshInstances() {
        if (this.scene) {
            this.groupControls.forEach(groupControlToReset => {
                if (groupControlToReset) {
                    groupControlToReset.gizmoMesh.scaling = new Vector3(1, 1, 1);
                }

                this.disposeGroupIdMeshes(groupControlToReset.groupId);
            });
        }
    }

    removeAllGroupControls() {
        this.groupControls.forEach(groupCtrl => {
            if (groupCtrl) {
                groupCtrl.groupGizmo.scaleGizmoEnabled = false;
                groupCtrl.groupGizmo.positionGizmoEnabled = false;
                groupCtrl.groupGizmo.rotationGizmoEnabled = false;
            }
        })

        this.groupControls = []
    }

    cleanMeshInstancesWithGroupId(groupId: string | number) {
        if (this.scene) {
            const groupControlToReset = this.groupControls.find(s => s.groupId === groupId);
            if (groupControlToReset) {
                groupControlToReset.gizmoMesh.scaling = new Vector3(1, 1, 1);
            }

            this.disposeGroupIdMeshes(groupId);
        }
    }

    private disposeGroupIdMeshes(groupId: string | number) {
        const goodMeshes = this.scene.meshes.filter(s => (!s.metadata) || (s.metadata && (s.metadata as MetadataModel).groupId !== groupId));
        const badMeshes = this.scene.meshes.filter(s => (s.metadata && (s.metadata as MetadataModel).groupId === groupId));
        badMeshes.forEach(mesh => {
            mesh.dispose();
            this.scene?.removeMesh(mesh);
        });
        this.scene.meshes = goodMeshes;
        this.disposeMeshInstances();
    }

    removeGroupControl(groupId: string | number) {
        // we dont dispose as that seems to have decremental effect on whole utility layer and all gizmos dissappear
        const groupCtrl = this.groupControls.find(g => g.groupId === groupId);
        if (groupCtrl) {
            groupCtrl.groupGizmo.scaleGizmoEnabled = false;
            groupCtrl.groupGizmo.positionGizmoEnabled = false;
            groupCtrl.groupGizmo.rotationGizmoEnabled = false;
            this.groupControls = this.groupControls.filter(s => s.groupId !== groupId);
        }
    }


    getGroupDimensions(groupId: string | number, update: boolean = true): any {
        let groupControl = this.groupControls.find(s => s.groupId === groupId);
        const info = groupControl.gizmoMesh.getHierarchyBoundingVectors();
        return info;
    }

    getGizmoBoxDimensions(groupId: string | number, update: boolean = true): Point3 {
        let groupControl = this.groupControls.find(s => s.groupId === groupId);
        let result: Point3 = [0, 0, 0];
        if (groupControl) {
            if (update) {
                // we need to force the update on gizmo
                groupControl.groupGizmo.attachableMeshes = [];
                groupControl.groupGizmo.attachToMesh(groupControl.gizmoMesh);
                groupControl?.groupGizmo?.gizmos?.boundingBoxGizmo?.updateBoundingBox();
            }
            const bboxDimensions = (groupControl?.groupGizmo.gizmos.boundingBoxGizmo as any)._boundingDimensions;
            if (update) {
                // afterwards we need to detach and set attachable meshes back
                groupControl.groupGizmo.attachToMesh(null);
                groupControl.groupGizmo.attachableMeshes = [groupControl.gizmoMesh];
            }
            result = [bboxDimensions._x, bboxDimensions._y, bboxDimensions._z];;
        }
        return result;
    }

    createCustomBoxMesh(groupId, width, length, height, metadata: MetadataModel, origin: Point3, locationInSite: { position: Point3, rotation: Point3 }, partTransformation?: PartTransformation) {
        const customMesh = MeshBuilder.CreateBox('customMesh' + Math.random(), { width, depth: length, height });
        this.shadowGenerator?.addShadowCaster(customMesh, false);
        let groupControl = this.groupControls.find(s => s.groupId === groupId);
        if (!groupControl) {
            const gizmoMesh = new Mesh('gizmo' + groupId, this.scene);
            gizmoMesh.position = new Vector3(locationInSite.position[0], locationInSite.position[1], locationInSite.position[2]);
            gizmoMesh.rotation = new Vector3(locationInSite.rotation[0], locationInSite.rotation[1], locationInSite.rotation[2]);

            gizmoMesh.isVisible = false;
            gizmoMesh.metadata = { groupId };
            const groupGizmo = this.createGizmo(gizmoMesh, groupId);
            if (groupGizmo) {
                groupControl = { groupId: groupId, gizmoMesh, groupGizmo };
                this.groupControls.push(groupControl);
                customMesh.parent = groupControl.gizmoMesh;
            }
        } else {
            customMesh.parent = groupControl.gizmoMesh;
        }

        this.applyPartTransformation(partTransformation, metadata, groupControl, customMesh);

        customMesh.metadata = metadata;
        customMesh.material = this.customMeshMaterial;
        customMesh.position = new Vector3(origin[0], origin[1], origin[2]);
        customMesh.receiveShadows = true;

        return customMesh;
    }


    disableGizmosInAllGroupControls() {
        this.groupControls.forEach(ctrl => {
            ctrl.groupGizmo.positionGizmoEnabled = false;
            ctrl.groupGizmo.rotationGizmoEnabled = false;
            ctrl.groupGizmo.boundingBoxGizmoEnabled = false;
            ctrl.gizmoMesh.isPickable = false;
        });
    }

    enableGizmosInAllGroupControls() {
        this.groupControls.forEach(ctrl => {
            ctrl.groupGizmo.positionGizmoEnabled = true;
            ctrl.groupGizmo.rotationGizmoEnabled = true;
            ctrl.groupGizmo.boundingBoxGizmoEnabled = true;
            ctrl.gizmoMesh.isPickable = true;
        });
    }

    disposeMeshCache() {
        this.meshCache.forEach(
            mc => {
                mc.mesh.dispose();
                mc.instancedMeshes.forEach(mi => {
                    mi.dispose();
                })
            }
        )
        this.meshCache = [];
    }

    disposeMeshInstances() {
        this.meshCache.forEach(
            mc => {
                mc.instancedMeshes.forEach(mi => {
                    mi.dispose();
                });
                mc.instancedMeshes = [];
            }
        )
    }
}